import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TooltipTrigger,
  Tooltip,
  cn,
  TooltipContent,
} from '@newfront-insurance/core-ui/v2';
import { ChevronDown, XIcon } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';

import { SearchRow } from './search-row';
import { type NovaSearchAccountResult, type SelectedAccount, useAccountSearch } from './use-account-search';

export interface AccountSelectorProps {
  selectedAccount: SelectedAccount;
  onSelectAccount: (account: SelectedAccount) => void;
  loggedInUserUuid: string;
  userScopes: string[];
  disabled?: boolean;
  showAnimation?: boolean;
  className?: string;
  containerClassName?: string;
  width?: number;
  placeholder?: string;
}

export function AccountSelector({
  width,
  placeholder = 'Click to select an account',
  selectedAccount,
  onSelectAccount,
  loggedInUserUuid,
  userScopes,
  disabled = false,
  showAnimation = false,
  className,
  containerClassName,
}: AccountSelectorProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { handleSearch, handleReset, isLoading, results, error } = useAccountSearch({
    loggedInUserUuid,
    userScopes,
  });

  const selectedAccountLabel: string = selectedAccount?.label ?? placeholder;

  const handleInputChange = (value: string) => {
    setSearchValue(value);
    handleSearch(value);
  };

  const handleAccountSelect = (account: NovaSearchAccountResult | null) => {
    onSelectAccount(account);
    setIsOpen(false);
    setSearchValue('');
    handleReset();
  };

  const handleOpenChange = (open: boolean) => {
    if (disabled && open) {
      return;
    }

    setIsOpen(open);
    if (!open) {
      setSearchValue('');
      handleReset();
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div className={cn('w-fit min-w-0 sm:mx-auto', containerClassName)}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="relative w-full min-w-0">
                <Button
                  variant="secondary"
                  size="default"
                  disabled={disabled}
                  className={cn(
                    'flex-basis-[300px] relative max-w-[300px] min-w-0 shrink justify-between px-4 pr-10 text-left font-normal sm:min-w-[300px]',
                    {
                      'text-muted-foreground': !selectedAccount,
                      'border-blue-300 bg-blue-50/50': showAnimation && !disabled,
                    },
                    className,
                  )}
                  style={{ width }}
                  title={selectedAccountLabel}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span className="text-steel-500 truncate text-ellipsis">{selectedAccountLabel}</span>
                  </div>
                  <ChevronDown className="absolute top-3 right-2 h-4 w-4 flex-shrink-0" />
                </Button>
                {selectedAccount && !disabled && (
                  <Button
                    variant="transparent"
                    className="absolute top-0 right-6 p-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();

                      handleAccountSelect(null);
                    }}
                  >
                    <XIcon width={16} height={16} />
                  </Button>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className={cn({ hidden: !disabled })}>
              <p>Create a new chat to change the account</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </PopoverTrigger>

      <PopoverContent className="p-0 lg:w-[300px]" align="start">
        <div className="p-2">
          <Input
            type="search"
            placeholder="Search for account to chat with..."
            value={searchValue}
            onChange={(e) => handleInputChange(e.target.value)}
            autoFocus
          />
        </div>

        <div className="max-h-60 overflow-y-auto">
          {error && <div className="text-fire-500 p-3 text-sm">{error.data.errorMessage}</div>}

          {isLoading && <div className="text-muted-foreground p-3 text-sm">Searching accounts...</div>}

          {!isLoading && !error && searchValue && (!results || results.length === 0) && (
            <div className="text-muted-foreground p-3 text-sm">No accounts found</div>
          )}

          {results?.map((account) => (
            <SearchRow
              key={account.uuid}
              account={account}
              isSelected={selectedAccount?.uuid === account.uuid}
              onClick={() => handleAccountSelect(account)}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
